import React, { createContext, useContext, useState, useEffect } from 'react';

const OnboardingContext = createContext();

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

export const OnboardingProvider = ({ children }) => {
    // Track completed tours to avoid showing them again
    const [completedTours, setCompletedTours] = useState(() => {
        try {
            const saved = localStorage.getItem('onboarding_completed_tours');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            console.error('Failed to parse completed tours', e);
            return [];
        }
    });

    // Active tour state
    const [activeTour, setActiveTour] = useState(null);
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    const [globalDismissed, setGlobalDismissed] = useState(() => {
        // Tours are ALWAYS dismissed by default — they only launch when manually triggered
        // via the Guide button (which uses restartTour, bypassing this flag).
        // We keep reading localStorage so if someone had previously dismissed, it persists,
        // but the default is now true (never auto-show) regardless.
        return true;
    });

    useEffect(() => {
        localStorage.setItem('onboarding_completed_tours', JSON.stringify(completedTours));
    }, [completedTours]);

    useEffect(() => {
        localStorage.setItem('onboarding_global_dismissed', globalDismissed.toString());
    }, [globalDismissed]);

    const startTour = (tourId, force = false) => {
        // Auto-start is fully disabled — tours only launch via restartTour (Guide button).
        // Kept as a no-op so existing callsites don't break.
    };

    const restartTour = (tourId) => {
        // For manual restarts, we ignore the global dismissal
        setActiveTour(tourId);
        setRunTour(true);
        setStepIndex(0);
    };

    const completeTour = (tourId) => {
        if (!completedTours.includes(tourId)) {
            setCompletedTours((prev) => [...prev, tourId]);
        }
        // Once ANY tour is complete or skipped, we globally dismiss automatic tours
        setGlobalDismissed(true);
        setRunTour(false);
        setActiveTour(null);
    };

    const stopTour = () => {
        // If they just close/stop it, we also consider it a global dismissal of auto-popping tours
        setGlobalDismissed(true);
        setRunTour(false);
        setActiveTour(null);
    };

    const resetAllTours = () => {
        setCompletedTours([]);
        localStorage.removeItem('onboarding_completed_tours');
        localStorage.removeItem('onboarding_global_dismissed');
    };

    const value = {
        activeTour,
        runTour,
        stepIndex,
        completedTours,
        startTour,
        restartTour,
        stopTour,
        completeTour,
        resetAllTours,
        setStepIndex,
    };

    return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};
